import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Lightweight, focused dashboard component: normalized devices, per-device caldera state,
// websocket handling and Honeywell-like sequence -> monotonic progress mapping.

export default function Dashboard() {
  const navigate = useNavigate();

  // Devices and per-device caldera states
  const [devices, setDevices] = useState([]);
  const devicesRef = useRef([]);
  const [calderas, setCalderas] = useState({}); // keyed by token/device_id/id
  const calderaPrevRef = useRef({}); // per-device prevProgress/prevStage/prevError

  // Legacy single-caldera UI compatibility (kept minimal)
  const [calderaProgress, setCalderaProgress] = useState(0);
  const [calderaStatus, setCalderaStatus] = useState('OFFLINE');
  const [calderaError, setCalderaError] = useState(null);
  const [etapaNombre, setEtapaNombre] = useState('-');
  const [calderaSequenceName, setCalderaSequenceName] = useState('-');
  const [calderaSequenceTime, setCalderaSequenceTime] = useState(0);
  const [calderaFlameVoltage, setCalderaFlameVoltage] = useState(null);
  const [calderaTerminals, setCalderaTerminals] = useState({});
  const [calderaErrorCode, setCalderaErrorCode] = useState(null);
  const [calderaOnline, setCalderaOnline] = useState(false);

  // Alerts / stats
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, alerts: 0 });

  // refs for monotonic tracking when legacy single-caldera used
  const prevProgressRef = useRef(0);
  const prevStageRef = useRef(null);
  const prevErrorRef = useRef(null);

  // Helpers: normalize backend device rows into consistent frontend shape
  function normalizeDevices(rows) {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => {
      const token = row.token ?? row.device_token ?? null;
      const numericId = (row.device_id ?? row.id) != null ? Number(row.device_id ?? row.id) : null;
      return ({
        id: numericId,
        device_id: numericId,
        token,
        name: row.name ?? token ?? `Dispositivo ${numericId ?? token ?? '—'}`,
        type: row.device_type ?? row.type ?? null,
        location: row.location ?? '',
        is_online: (row.device_is_online !== undefined) ? Boolean(row.device_is_online) : Boolean(row.is_online ?? 0),
        status_is_online: (row.status_is_online !== undefined) ? Boolean(row.status_is_online) : Boolean(row.is_online ?? 0),
        led_state: row.led_state ?? null,
        registers: row.registers ?? {},
        _raw: row
      });
    });
  }

  function isCalderaDevice(row) {
    if (!row) return false;
    const name = (row.name || '').toString().toLowerCase();
    const type = (row.type || '').toString().toLowerCase();
    if (name.includes('caldera') || name.includes('calder') || type.includes('boiler') || type.includes('caldera')) return true;
    // fallback: presence of registers typical for devices
    if (row.registers && Object.keys(row.registers).length > 0) return true;
    return false;
  }

  const mapStatusToProgress = useCallback((s) => {
    if (!s) return 0;
    const st = s.toString().toUpperCase();
    if (st.includes('STANDBY') || st.includes('ESPERA')) return 10;
    if (st.includes('PURGE') || st.includes('PURGA')) return 30;
    if (st.includes('PILOT') || st.includes('IGNIT') || st.includes('IGNIC')) return 60;
    if (st.includes('MAIN') || st.includes('PRINCIPAL')) return 80;
    if (st.includes('RUN') || st.includes('MARCHA')) return 100;
    if (st.includes('INICIO')) return 0;
    return 0;
  }, []);

  const mapStatusToLabel = useCallback((s) => {
    if (!s) return 'Desconocida';
    const st = s.toString().toUpperCase();
    if (st.includes('STANDBY') || st.includes('ESPERA')) return 'Espera (Standby)';
    if (st.includes('PURGE') || st.includes('PURGA')) return 'Purgado de gases (Purge)';
    if (st.includes('PILOT') || st.includes('IGNIT') || st.includes('IGNIC')) return 'Encendido piloto (Pilot)';
    if (st.includes('MAIN') || st.includes('PRINCIPAL')) return 'Llama principal (Main)';
    if (st.includes('RUN') || st.includes('MARCHA')) return 'Operando (Run)';
    if (st.includes('FAULT')) return 'Falla';
    return s;
  }, []);

  // Compute monotonic progress from interpreted snapshot for a single device
  function computeProgressFromInterpDevice(i, prevProgress) {
    try {
      const explicit = Number(i.progressPercent ?? i.progress ?? 0) || 0;
      if (explicit > 0) return Math.min(Math.max(explicit, 0), 100);
      const seqName = (i.sequenceName ?? i.sequence_name ?? i.status ?? i.stage ?? '').toString().toUpperCase();
      const t = Number(i.sequenceTimeSeconds ?? i.sequence_time_seconds ?? i.sequenceTime ?? 0);

      if (seqName.includes('HOLD')) return Math.round(prevProgress || 0);

      const stages = [
        { match: ['INICIO'], min: 0, max: 10, maxTime: 10, advance: true },
        { match: ['ESPERA','STANDBY'], advance: false, holdAt: () => (prevProgress || mapStatusToProgress('STANDBY') || 0) },
        { match: ['PURGA HOLD','PURGA HOLD HIGH','PURGA HOLD: FUEGO ALTO','PURGE HOLD','HIGH FIRE'], advance: false, holdAt: () => (prevProgress || 0) },
        { match: ['PURGA','POSTPURGA','PURGE','PURGE DELAY'], min: 10, max: 40, maxTime: 30, advance: true },
        { match: ['PURGA HOLD LOW','FUEGO BAJO','PURGA HOLD: FUEGO BAJA'], advance: false, holdAt: () => (prevProgress || 40) },
        { match: ['IGNICION_PILOTO','PILOT','PILOTO','IGNICION_PILOTO'], min: 40, max: 70, maxTime: 10, advance: true },
        { match: ['IGNICION_PRINCIPAL','MAIN','PRINCIPAL'], min: 70, max: 90, maxTime: 10, advance: true },
        { match: ['MARCHA','RUN'], min: 90, max: 100, maxTime: 1, advance: true }
      ];

      let computed = null;
      for (const s of stages) {
        for (const m of s.match) {
          if (seqName.includes(m)) {
            if (s.advance === false) {
              try { computed = Number(s.holdAt()); } catch (e) { computed = Number(prevProgress || 0); }
            } else {
              const mt = Math.max(1, s.maxTime || 1);
              const ratio = Math.min(1, Math.max(0, t / mt));
              const p = s.min + ratio * (s.max - s.min);
              computed = p;
            }
            break;
          }
        }
        if (computed !== null) break;
      }

      if (computed === null) computed = mapStatusToProgress(seqName || '');
      const nonDec = Math.max(prevProgress || 0, Number(computed || 0));
      return Math.round(nonDec);
    } catch (e) {
      return Math.round(prevProgress || 0);
    }
  }

  // Update per-device caldera state from interpreted payload
  function updateCalderaFromInterp(deviceKey, interp) {
    try {
      const key = deviceKey ? String(deviceKey) : null;
      const statusRaw = interp.status ?? interp.state ?? interp.stage ?? interp.statusName ?? null;
      const statusStr = statusRaw ? String(statusRaw).toUpperCase() : null;
      const errorDesc = interp.errorDescription || interp.error || interp.error_description || null;
      const etapaLabel = mapStatusToLabel(statusStr || interp.status || interp.stage || interp.state);

      const prevRefs = key ? (calderaPrevRef.current[key] || { prevProgress: 0, prevStage: null, prevError: null }) : { prevProgress: prevProgressRef.current || 0, prevStage: prevStageRef.current || null, prevError: prevErrorRef.current || null };

      // INICIO resets
      const etapaUpperPre = (etapaLabel || '').toString().toUpperCase();
      if (etapaUpperPre.includes('INICIO')) prevRefs.prevProgress = 0;

      // Fault -> STANDBY recovery mapping
      const hadPrevError = prevRefs.prevError || false;
      if ((hadPrevError || false) && statusStr && (statusStr.includes('STANDBY') || statusStr.includes('ESPERA'))) {
        prevRefs.prevProgress = mapStatusToProgress('STANDBY') || 0;
        prevRefs.prevError = null;
      }

      let progress = computeProgressFromInterpDevice(interp, prevRefs.prevProgress);
      if (errorDesc) {
        progress = prevRefs.prevProgress || progress || 0;
      } else {
        prevRefs.prevProgress = Math.max(prevRefs.prevProgress || 0, progress || 0);
      }

      const status = (errorDesc ? 'FAULT' : (interp.online ? 'RUNNING' : (statusStr || 'OFFLINE')));

      const calderaState = {
        progress: Math.round(progress),
        status,
        error: errorDesc,
        etapaNombre: etapaLabel,
        sequenceName: interp.sequenceName ?? interp.sequence_name ?? interp.sequence ?? '-',
        sequenceTime: Number(interp.sequenceTimeSeconds ?? interp.sequence_time_seconds ?? interp.sequenceTime ?? 0) || 0,
        flameVoltage: interp.flameVoltage ?? interp.flame_voltage ?? null,
        terminals: interp.terminals ?? {},
        errorCode: interp.errorCode ?? interp.error_code ?? null,
        online: Boolean(interp.online)
      };

      if (key) {
        calderaPrevRef.current[key] = prevRefs;
        setCalderas(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...calderaState } }));
      } else {
        // fallback to legacy single caldera vars
        prevProgressRef.current = prevRefs.prevProgress;
        prevStageRef.current = prevRefs.prevStage;
        prevErrorRef.current = prevRefs.prevError;
        setCalderaProgress(calderaState.progress);
        setCalderaStatus(calderaState.status);
        setCalderaError(calderaState.error);
        setEtapaNombre(calderaState.etapaNombre);
        setCalderaSequenceName(calderaState.sequenceName);
        setCalderaSequenceTime(calderaState.sequenceTime);
        setCalderaFlameVoltage(calderaState.flameVoltage);
        setCalderaTerminals(calderaState.terminals);
        setCalderaErrorCode(calderaState.errorCode);
        setCalderaOnline(calderaState.online);
      }

      // alerts on stage change
      if (prevRefs.prevStage !== etapaLabel) {
        const title = `Etapa: ${etapaLabel}`;
        const desc = prevRefs.prevStage ? `Cambiada desde '${prevRefs.prevStage}'` : `Iniciada etapa '${etapaLabel}'`;
        setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0, 8));
        setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
        prevRefs.prevStage = etapaLabel;
      }

      // alerts on new error
      if (errorDesc && prevRefs.prevError !== errorDesc) {
        const title = `Falla detectada`;
        const desc = `${errorDesc}`;
        setAlerts(a => [{ title, description: desc, time: new Date().toISOString() }, ...a].slice(0, 8));
        setStats(s => ({ ...s, alerts: (s.alerts || 0) + 1 }));
        prevRefs.prevError = errorDesc;
        if (key) setCalderas(prev => ({ ...prev, [key]: { ...(prev[key] || {}), status: 'FAULT' } }));
        else setCalderaStatus('FAULT');
      }
    } catch (e) {
      console.warn('updateCalderaFromInterp error', e);
    }
  }

  // Toggle LED (admin-only in UI) - optimistic update and revert
  async function toggleLed(deviceId, currentState, role) {
    if (role && role !== 'admin') {
      setAlerts(a => [{ title: 'Permisos', description: 'No tiene permiso para cambiar este dispositivo', time: new Date().toISOString() }, ...a].slice(0, 8));
      return;
    }
    const target = !currentState;
    const idStr = String(deviceId);
    setDevices(prev => prev.map(d => ((String(d.id) === idStr || String(d.device_id) === idStr || String(d.token) === idStr) ? ({ ...d, led_state: target }) : d)));
    try {
      const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const urls = [`${base}/api/devices/${deviceId}/toggle`, `${base}/devices/${deviceId}/toggle`];
      let ok = false;
      for (const u of urls) {
        try {
          const r = await fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ state: target }) });
          if (r.ok) { ok = true; break; }
        } catch (e) { continue; }
      }
      if (!ok) throw new Error('No se pudo contactar API');
    } catch (err) {
      setAlerts(a => [{ title: 'Error', description: 'No se pudo cambiar el estado del LED', time: new Date().toISOString() }, ...a].slice(0, 8));
      setDevices(prev => prev.map(d => ((String(d.id) === idStr || String(d.device_id) === idStr || String(d.token) === idStr) ? ({ ...d, led_state: currentState }) : d)));
    }
  }

  // Initial load: try JWT-backed endpoints (/devices/me) then fallback to user/:id/devices
  useEffect(() => {
    const apiBaseRaw = process.env.REACT_APP_API_BASE_URL || '';
    const apiBase = apiBaseRaw.replace(/\/$/, '');

    async function loadInitial() {
      try {
        const rawUser = localStorage.getItem('user');
        let storedToken = localStorage.getItem('token') || null;
        try { const parsed = rawUser ? JSON.parse(rawUser) : null; if (!storedToken && parsed) storedToken = parsed.token ?? parsed.accessToken ?? parsed.jwt ?? parsed.authToken ?? null; } catch(e) {}

        let fetched = false;
        if (storedToken) {
          const tryMeUrls = [`${apiBase}/api/devices/me`, `${apiBase}/devices/me`, `${apiBase}/api/me/devices`, `${apiBase}/me/devices`];
          for (const u of tryMeUrls) {
            try {
              const r = await fetch(u, { headers: { Authorization: `Bearer ${storedToken}` } });
              if (!r.ok) continue;
              const j = await r.json();
              const rows = Array.isArray(j.devices) ? j.devices : (Array.isArray(j) ? j : (j.devices ? j.devices : null));
              if (rows !== null) {
                const norm = normalizeDevices(rows);
                setDevices(norm);
                setStats(prev => ({ ...prev, total: norm.length }));
                norm.forEach(row => {
                  const key = String(row.token ?? row.device_id ?? row.id);
                  if (!key) return;
                  if (!isCalderaDevice(row)) return;
                  const fakeInterp = { status: (row.status_is_online ? 'RUNNING' : 'OFFLINE'), sequenceName: '-', sequenceTimeSeconds: 0, errorDescription: null, online: !!row.status_is_online };
                  updateCalderaFromInterp(key, fakeInterp);
                });
                fetched = true;
                break;
              }
            } catch (e) { continue; }
          }
        }

        if (!fetched) {
          let userId = null;
          try { userId = rawUser ? JSON.parse(rawUser).id : null; } catch (e) { userId = null; }
          if (userId) {
            const tryUrls = [`${apiBase}/api/user/${userId}/devices`, `${apiBase}/user/${userId}/devices`];
            for (const u of tryUrls) {
              try {
                const r = await fetch(u);
                if (!r.ok) continue;
                const j = await r.json();
                const rows = Array.isArray(j.devices) ? j.devices : (Array.isArray(j) ? j : null);
                if (rows !== null) {
                  const norm = normalizeDevices(rows);
                  setDevices(norm);
                  setStats(prev => ({ ...prev, total: norm.length }));
                  norm.forEach(row => {
                    const key = String(row.token ?? row.device_id ?? row.id);
                    if (!key) return;
                    if (!isCalderaDevice(row)) return;
                    if (row.interpreted) updateCalderaFromInterp(key, row.interpreted);
                    else if (row.registers) {
                      const registers = row.registers || {};
                      const regStage = registers['2'] ?? registers[2] ?? registers['3'] ?? registers[3] ?? null;
                      const startupCounter = Number(registers['5'] ?? registers['startupCounter'] ?? registers['start'] ?? 0);
                      const errorCode = Number(registers['0'] ?? registers['error'] ?? 0);
                      let fallbackStatus = 'OFFLINE';
                      if (errorCode !== 0) fallbackStatus = 'FAULT';
                      else if (regStage) {
                        const rs = String(regStage).toUpperCase();
                        if (rs.includes('HOLD')) fallbackStatus = 'PURGE_HOLD';
                        else if (rs.includes('PURGE') || rs.includes('PURGA')) fallbackStatus = 'PURGE';
                        else if (rs.includes('PILOT') || rs.includes('PILOTO') || rs.includes('IGNIT')) fallbackStatus = 'PILOT';
                        else if (rs.includes('MAIN') || rs.includes('PRINCIPAL')) fallbackStatus = 'MAIN';
                        else if (rs.includes('MARCHA') || rs.includes('RUN')) fallbackStatus = 'RUN';
                        else if (rs.includes('STANDBY') || rs.includes('ESPERA')) fallbackStatus = 'STANDBY';
                      } else if (startupCounter > 0 && startupCounter < 30) fallbackStatus = 'STARTING';
                      else if (startupCounter >= 30) fallbackStatus = 'RUNNING';

                      const fakeInterp = { status: fallbackStatus, sequenceName: regStage ? String(regStage) : null, sequenceTimeSeconds: startupCounter, errorDescription: errorCode !== 0 ? `Falla código ${errorCode}` : null, online: true };
                      if (String(fallbackStatus).toUpperCase().includes('HOLD')) fakeInterp.progress = calderaPrevRef.current[String(key)]?.prevProgress || 0;
                      else fakeInterp.progress = mapStatusToProgress(fallbackStatus);
                      updateCalderaFromInterp(key, fakeInterp);
                    }
                  });
                  break;
                }
              } catch (e) { continue; }
            }
          }
        }
      } catch (e) {
        // ignore; realtime ws will populate
      }

      try {
        const statsRes = await fetch(`${apiBase}/api/dashboard/stats`);
        if (statsRes.ok) {
          const js = await statsRes.json();
          setStats(prev => ({ ...prev, ...js }));
        }
      } catch (e) {}
    }

    loadInitial();
  }, []);

  // Keep devicesRef and prune calderas for only assigned devices
  useEffect(() => {
    devicesRef.current = devices;
    setStats(prev => ({ ...prev, total: devices.length }));
    const keys = new Set(devices.map(d => String(d.token ?? d.device_id ?? d.id)));
    setCalderas(prev => {
      const next = {};
      Object.keys(prev).forEach(k => { if (keys.has(String(k))) next[k] = prev[k]; });
      return next;
    });
  }, [devices]);

  // Mirror first caldera into legacy single-caldera vars for compatibility
  useEffect(() => {
    const keys = Object.keys(calderas);
    if (keys.length > 0) {
      const k = keys[0];
      const c = calderas[k];
      if (c) {
        setCalderaProgress(c.progress);
        setCalderaStatus(c.status);
        setCalderaError(c.error);
        setEtapaNombre(c.etapaNombre);
        setCalderaSequenceName(c.sequenceName);
        setCalderaSequenceTime(c.sequenceTime);
        setCalderaFlameVoltage(c.flameVoltage);
        setCalderaTerminals(c.terminals);
        setCalderaErrorCode(c.errorCode);
        setCalderaOnline(c.online);
      }
    } else {
      setCalderaProgress(0);
      setCalderaStatus('OFFLINE');
      setCalderaError(null);
      setEtapaNombre('-');
    }
  }, [calderas]);

  // WebSocket: single connection, route interpreted/device updates per-device
  useEffect(() => {
    let ws;
    let reconnectTimer = null;
    const rawWs = (process.env.REACT_APP_WS_URL || '').trim();
    let wsUrl = '';
    if (rawWs) {
      if (rawWs.startsWith('ws://') || rawWs.startsWith('wss://')) wsUrl = rawWs.replace(/\/$/, '');
      else if (rawWs.startsWith('http://')) wsUrl = 'ws://' + rawWs.replace(/^http:\/\//, '').replace(/\/$/, '') + '/ws';
      else if (rawWs.startsWith('https://')) wsUrl = 'wss://' + rawWs.replace(/^https:\/\//, '').replace(/\/$/, '') + '/ws';
      else wsUrl = rawWs.replace(/\/$/, '');
    } else wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

    function scheduleReconnect() { if (reconnectTimer) clearTimeout(reconnectTimer); reconnectTimer = setTimeout(connect, 3000); }

    function connect() {
      try { ws = new WebSocket(wsUrl); } catch (e) { scheduleReconnect(); return; }
      ws.onopen = () => {
        const rawUser = localStorage.getItem('user'); let userId = null; try { userId = rawUser ? JSON.parse(rawUser).id : null; } catch(e) { userId = null; }
        const snap = userId ? { type: 'snapshot', user_id: userId } : { type: 'snapshot' };
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(snap));
      };

      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          // interpreted_state messages
          if (payload.type === 'interpreted_state') {
            const data = payload.data || payload;
            const iden = payload.deviceId ?? payload.device_id ?? payload.token ?? data.deviceId ?? data.device_id ?? data.token ?? null;
            let key = null;
            if (iden) {
              const idStr = String(iden);
              const found = devicesRef.current.find(d => [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr));
              key = found ? (found.token ?? found.device_id ?? found.id) : idStr;
            }
            updateCalderaFromInterp(key, data);
            return;
          }

          // device update payloads (interpreted preferred)
          if (payload.type === 'DEVICE_UPDATE' || payload.type === 'device_update') {
            const deviceId = payload.deviceId ?? payload.device_id ?? payload.token ?? null;
            const interpreted = payload.interpreted || payload.interpretedState || payload.interpreted_state || null;
            let key = null;
            if (deviceId) {
              const idStr = String(deviceId);
              const found = devicesRef.current.find(d => [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr));
              key = found ? (found.token ?? found.device_id ?? found.id) : idStr;
            }
            if (interpreted) updateCalderaFromInterp(key, interpreted);
            else if (payload.registers) {
              // best-effort fallback: synthesize interpreted from registers
              const registers = payload.registers || {};
              const regStage = registers['2'] ?? registers[2] ?? registers['3'] ?? registers[3] ?? null;
              const startupCounter = Number(registers['5'] ?? registers['startupCounter'] ?? registers['start'] ?? 0);
              const errorCode = Number(registers['0'] ?? registers['error'] ?? 0);
              let fallbackStatus = 'OFFLINE';
              if (errorCode !== 0) fallbackStatus = 'FAULT';
              else if (regStage) {
                const rs = String(regStage).toUpperCase();
                if (rs.includes('HOLD')) fallbackStatus = 'PURGE_HOLD';
                else if (rs.includes('PURGE') || rs.includes('PURGA')) fallbackStatus = 'PURGE';
                else if (rs.includes('PILOT') || rs.includes('PILOTO') || rs.includes('IGNIT')) fallbackStatus = 'PILOT';
                else if (rs.includes('MAIN') || rs.includes('PRINCIPAL')) fallbackStatus = 'MAIN';
                else if (rs.includes('MARCHA') || rs.includes('RUN')) fallbackStatus = 'RUN';
                else if (rs.includes('STANDBY') || rs.includes('ESPERA')) fallbackStatus = 'STANDBY';
              } else if (startupCounter > 0 && startupCounter < 30) fallbackStatus = 'STARTING';
              else if (startupCounter >= 30) fallbackStatus = 'RUNNING';

              const fakeInterp = { status: fallbackStatus, sequenceName: regStage ? String(regStage) : null, sequenceTimeSeconds: startupCounter, errorDescription: errorCode !== 0 ? `Falla código ${errorCode}` : null, online: true };
              if (String(fallbackStatus).toUpperCase().includes('HOLD')) fakeInterp.progress = calderaPrevRef.current[String(key)]?.prevProgress || 0;
              else fakeInterp.progress = mapStatusToProgress(fallbackStatus);
              updateCalderaFromInterp(key, fakeInterp);
            }
            return;
          }

          // simple events: online/offline/falla/stats/devices
          if (payload.type === 'offline' || payload.event === 'offline') {
            const did = payload.deviceId ?? payload.device_id ?? (payload.data && (payload.data.deviceId ?? payload.data.device_id)) ?? null;
            if (did) {
              const idStr = String(did);
              setDevices(prev => prev.map(d => (([d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr)) ? ({ ...d, status_is_online: 0 }) : d)));
              const found = devicesRef.current.find(d => [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr));
              const key = found ? (found.token ?? found.device_id ?? found.id) : String(idStr);
              if (key) setCalderas(prev => ({ ...prev, [key]: { ...(prev[key] || {}), status: 'OFFLINE', error: null, progress: 0 } }));
            }
          }

          if (payload.type === 'online' || payload.event === 'online') {
            const did = payload.deviceId ?? payload.device_id ?? (payload.data && (payload.data.deviceId ?? payload.data.device_id)) ?? null;
            if (did) {
              const idStr = String(did);
              setDevices(prev => prev.map(d => (([d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr)) ? ({ ...d, status_is_online: 1 }) : d)));
              const found = devicesRef.current.find(d => [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr));
              const key = found ? (found.token ?? found.device_id ?? found.id) : String(idStr);
              if (key) setCalderas(prev => ({ ...prev, [key]: { ...(prev[key] || {}), status: 'STARTING', error: null } }));
            }
          }

          if (payload.type === 'falla' || payload.event === 'falla') {
            const code = payload.value ?? (payload.data && payload.data.errorCode) ?? null;
            const desc = payload.description || (payload.data && payload.data.errorDescription) || `Falla código ${code}`;
            const did = payload.deviceId ?? payload.device_id ?? (payload.data && (payload.data.deviceId ?? payload.data.device_id)) ?? null;
            if (did) {
              const idStr = String(did);
              const found = devicesRef.current.find(d => [d.id, d.device_id, d.token].some(x => x != null && String(x) === idStr));
              const key = found ? (found.token ?? found.device_id ?? found.id) : String(idStr);
              if (key) setCalderas(prev => ({ ...prev, [key]: { ...(prev[key] || {}), status: 'FAULT', error: desc } }));
            }
          }

          if (payload.type === 'stats' && payload.data) setStats(prev => ({ ...prev, ...payload.data }));
          if (payload.type === 'devices' && Array.isArray(payload.data)) {
            try { const norm = normalizeDevices(payload.data); setDevices(norm); setStats(prev => ({ ...prev, total: norm.length })); } catch (e) { setDevices(payload.data); }
          }
          if (payload.type === 'alert') { setStats(prev => ({ ...prev, alerts: (prev.alerts || 0) + 1 })); setAlerts(a => [{ title: payload.title || 'Alerta', description: payload.description || '', time: new Date().toISOString() }, ...a].slice(0, 8)); }
        } catch (e) { console.debug('ws message parse', e); }
      };

      ws.onclose = () => scheduleReconnect();
      ws.onerror = () => scheduleReconnect();
    }

    connect();
    return () => { if (reconnectTimer) clearTimeout(reconnectTimer); try { if (ws) ws.close(); } catch (e) {} };
  }, []);

  // Minimal UI: header + per-device caldera cards and device list
  return (
    <div style={{ padding: 20 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2>Dashboard</h2>
        <div>
          <button onClick={() => navigate('/')} style={{ marginRight: 8 }}>Volver</button>
        </div>
      </header>

      <section style={{ marginBottom: 20 }}>
        <h3>Calderas asignadas ({stats.total})</h3>
        {Object.keys(calderas).length === 0 && <div>No tiene calderas asignadas.</div>}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {Object.entries(calderas).map(([k, c]) => (
            <div key={k} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, minWidth: 220 }}>
              <div style={{ fontWeight: '600' }}>{c.sequenceName || k}</div>
              <div style={{ color: '#666', fontSize: 12 }}>{c.etapaNombre}</div>
              <div style={{ height: 12, background: '#f1f5f9', borderRadius: 6, marginTop: 8 }}>
                <div style={{ width: `${Math.max(0, Math.min(100, c.progress || 0))}%`, height: '100%', background: (String(c.status).toUpperCase().includes('FAULT') ? '#f56565' : '#38a169'), borderRadius: 6 }} />
              </div>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                <div>Progreso: {c.progress}%</div>
                <div>Estado: {c.status}</div>
                {c.error && <div style={{ color: '#c53030' }}>Falla: {c.error}</div>}
                {c.flameVoltage != null && <div>Flama: {c.flameVoltage}</div>}
                {c.errorCode != null && <div>Código: {c.errorCode}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3>Dispositivos</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th>ID</th>
              <th>Nombre</th>
              <th>Online</th>
              <th>LED</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {devices.map(d => (
              <tr key={String(d.token ?? d.device_id ?? d.id)} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: 8 }}>{d.device_id ?? d.id ?? d.token}</td>
                <td style={{ padding: 8 }}>{d.name}</td>
                <td style={{ padding: 8 }}>{d.status_is_online ? 'Sí' : 'No'}</td>
                <td style={{ padding: 8 }}>{d.led_state ? 'ON' : 'OFF'}</td>
                <td style={{ padding: 8 }}>
                  <button onClick={() => toggleLed(d.device_id ?? d.id ?? d.token, !!d.led_state, d._raw?.role)} style={{ marginRight: 8 }}>Toggle LED</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
